import Auth from "@aws-amplify/auth";
import store from "@/store";
import generateUser from "@/bin/aws/generateUser";
import bugsnagClient from "@/plugins/bugsnag";
const actions = {
  async getAWSUser() {
    const results = await Auth.currentAuthenticatedUser({ bypassCache: true });
    return results;
  },
  /**
   * Handles authentication through AWS-Amplify. Highly coupled.
   * @param {String} email Email of user that will be logging in. Auto-Lowered and Trimmed.
   * @param {String} password Password of user
   */
  async signin(email, password) {
    try {
      console.log("Signin...");
      await Auth.signIn(
        String(email)
          .trim()
          .toLowerCase(),
        String(password)
      );
      let x = await generateUser();

      console.log("Signin completed", x);
      return Promise.resolve(true);
    } catch (err) {
      if (err === "No current user") {
        console.log("[Signin]", err);
        bugsnagClient.notify(err, {
          metaData: {
            email,
            error: "No current user"
          }
        });
        throw new Error("Request failed. Please reach out to support@arist.co");
      }
      if (!err.code) {
        bugsnagClient.notify(err, {
          metaData: {
            email
          }
        });
        throw new Error("Unknown error occured, please try again later.");
      }
      switch (err.code) {
        case "NotAuthorizedException":
          throw new Error("Invalid email address or password.");
        case "UserNotFoundException":
          throw new Error("Account does not exist.");
        case "NetworkError":
          bugsnagClient.notify(err, {
            metaData: {
              email,
              code: err.code
            }
          });
          throw new Error(
            "Network error. Please try again. If it happens again, this can be caused by a firewall blocking our requests."
          );
        default:
          bugsnagClient.notify(err, {
            metaData: {
              email,
              code: err.code
            }
          });
          throw new Error("Could not process request. Please try again later.");
      }
    }
  },
  /**
   * This is for handling callbacks when doing SSO sign ins
   */
  async signUp(email, password) {
    try {
      await Auth.signUp({
        username: String(email)
          .toLowerCase()
          .trim()
          .replace(" ", ""),
        password: password,
        attributes: {
          email: String(email)
            .toLowerCase()
            .trim()
        }
      });
    } catch (err) {
      if (!err || !err.code) {
        bugsnagClient.notify(err, {
          metaData: {
            email
          }
        });
        throw new Error("Unknown error occured, please try again later.");
      }
      switch (err.code) {
        case "InvalidParameterException":
          throw new Error("Invalid email address or password.");
        case "UsernameExistsException":
          try {
            await actions.signin(email, password);
            return;
          } catch (err) {
            // failed to cheat
          }
          throw new Error("This email address is already in use. ");
        case "NetworkError":
          bugsnagClient.notify(err, {
            metaData: {
              email,
              code: err.code
            }
          });
          throw new Error(
            "Network error. Please try again. If it happens again, this can be caused by your networks firewall blocking our requests."
          );
        default:
          bugsnagClient.notify(err, {
            metaData: {
              email,
              code: err.code
            }
          });
          throw new Error("Could not create account. Try again later.");
      }
    }
  },
  /**
   * Handles user signout
   */
  async signout() {
    let data = await Auth.signOut().catch(err => {
      bugsnagClient.notify(err);
      console.error(err);
      clear();
      Promise.resolve(null);
    });
    console.log("Signout", data);
    store.commit("setAWSUser", null);
    store.commit("setUser", null);
    clear();

    //clear store
    return Promise.resolve(true);
  },
  /**
   *
   * @param {Object} param0
   * @param {String} param0.oldpassword
   * @param {String} param0.newpassword
   */
  async changePassword({ oldpassword, newpassword }) {
    try {
      let user = await Auth.currentAuthenticatedUser({ bypassCache: true });
      let result = await Auth.changePassword(user, oldpassword, newpassword);
      return result;
    } catch (err) {
      if (!err.code) {
        bugsnagClient.notify(err);
        throw new Error("Cannot change password at this time");
      }
      switch (err.code) {
        case "LimitExceededException":
          throw new Error("Number of requests exceeded limit, try again later");
        case "NotAuthorizedException":
          throw new Error("User is not authorized");
        case "PasswordResetRequiredException":
          throw new Error("Password needs to be reset");
        case "TooManyRequestsException":
          throw new Error("Rate of requests sent was exceeded");
        case "UserNotConfirmedException":
          bugsnagClient.notify(err);
          throw new Error("User is not confirmed");
        case "UserNotFoundException":
          bugsnagClient.notify(err);
          throw new Error("User is invalid");
        case "InvalidPasswordException":
          throw new Error("Password is invalid");
        default:
          bugsnagClient.notify(err);
          throw new Error("Cannot change password at this time");
      }
    }
  },
  /**
   *
   * @param {Object} param0
   * @param {String} param0.email
   */
  async forgotPassword({ email }) {
    try {
      let result = await Auth.forgotPassword(
        String(email)
          .toLowerCase()
          .trim()
      );
      return result;
    } catch (err) {
      if (!err.code) {
        bugsnagClient.notify(err, {
          metaData: {
            email
          }
        });
        throw new Error("Could not recover account at this time");
      }
      switch (err.code) {
        case "CodeDeliveryFailureException":
          bugsnagClient.notify(err, {
            metaData: {
              email,
              code: err.code,
              message: err.message
            }
          });
          throw new Error(
            "Could not deliver the recovery code to your account. Please reach out to support@arist.co"
          );
        case "InvalidParameterException":
          bugsnagClient.notify(err, {
            metaData: {
              email,
              code: err.code,
              message: err.message
            }
          });
          throw new Error("Invalid email, could not send request.");
        case "LimitExceededException":
          throw new Error(
            "There have been to many requests to reset this password, please try again later."
          );
        case "NotAuthorizedException":
          bugsnagClient.notify(err, {
            metaData: {
              email,
              code: err.code,
              message: err.message
            }
          });
          throw new Error("Not authorized for this action.");

        case "TooManyRequestsException":
          throw new Error(
            "To many requests have been submitted. Please wait 1 minute before trying again."
          );

        case "UserNotConfirmedException":
          bugsnagClient.notify(err, {
            metaData: {
              email,
              code: err.code,
              message: err.message
            }
          });
          throw new Error(
            "User was not confirmed during the signup process, please reach out to support@arist.co"
          );

        case "UserNotFoundException":
          bugsnagClient.notify(err, {
            metaData: {
              email,
              code: err.code,
              message: err.message
            }
          });
          throw new Error("User does not exist.");

        default:
          bugsnagClient.notify(err, {
            metaData: {
              email,
              code: err.code,
              message: err.message
            }
          });
          throw new Error(
            "An unknown error has occured, please reach out to support@arist.co"
          );
      }
    }
  },
  /**
   * Updates the user account with the new password.
   * @param {Object} param0
   * @param {String} param0.email
   * @param {String} param0.password
   * @param {String} param0.code
   */
  async forgotPasswordReset({ email, password, code }) {
    try {
      let result = await Auth.forgotPasswordSubmit(
        String(email)
          .toLocaleLowerCase()
          .trim(),
        code,
        password
      );
      return result;
    } catch (err) {
      if (!err.code) {
        bugsnagClient.notify(err, {
          metaData: {
            email
          }
        });
        throw new Error(
          "An unknwown error occured, please try again later. If this happens again, please contact support@arist.co"
        );
      }
      switch (err.code) {
        case "CodeMismatchException":
          throw new Error(
            "Invalid code. Make sure you are using the most recent one sent to your email."
          );
        case "UserNotFoundException":
          bugsnagClient.notify(err, {
            metaData: {
              email,
              code: err.code,
              message: err.message
            }
          });
          throw new Error("This account does not exist.");
        case "LimitExceededException":
          throw new Error(
            "To many reset requests have been sent. Please try again later."
          );
        case "InvalidParameterException":
          bugsnagClient.notify(err, {
            metaData: {
              email,
              code: err.code,
              message: err.message
            }
          });
          throw new Error("An unknown error occured. Please try again later.");
        case "InvalidPasswordException":
          throw new Error(
            "The password you entered is invalid. Please enter a valid password."
          );
        default:
          bugsnagClient.notify(err, {
            metaData: {
              email,
              code: err.code,
              message: err.message
            }
          });
          throw new Error(
            "We cannot process your request at this time, please try again later."
          );
      }
    }
  },
  async changeEmail({ newemail }) {
    try {
      let user = await Auth.currentAuthenticatedUser({ bypassCache: true });
      let result = await Auth.updateUserAttributes(user, {
        email: newemail
      });
      return result;
    } catch (err) {
      if (!err.code) {
        throw new Error("Cannot update email");
      }
      switch (err.code) {
        case "AliasExistsException":
          return new Error("This email address is already in use");
        case "NotAuthorizedException":
          bugsnagClient.notify(err);
          return new Error("Invalid user, cannot change email address");
        case "TooManyRequestsException":
          return new Error("To many requests, try again later");
        case "UserNotFoundException":
          bugsnagClient.notify(err);
          return new Error("User not found");
        default:
          bugsnagClient.notify(err);
          throw new Error("Cannot update email");
      }
    }
  },
  async verifyEmail({ email, code }) {
    // let user = await Auth.currentAuthenticatedUser({ bypassCache: true });
    let result = await Auth.verifyCurrentUserAttributeSubmit("email", code);
    return result;
  }
};
export default actions;
let domain = window.location.hostname;
if (process.env.VUE_APP_AUTH_COOKIE_DOMAIN != undefined) {
  domain = process.env.VUE_APP_AUTH_COOKIE_DOMAIN;
}
const clear = () => {
  var cookies = document.cookie.split(";");
  cookies.forEach(cookie => {
    let s = String(cookie).split(".");
    if (`${s[0]}`.trim() != "CognitoIdentityServiceProvider") {
      return;
    }
    console.log(
      domain,
      s[0],
      "CognitoIdentityServiceProvider",
      `${s[0]}`.trim() != "CognitoIdentityServiceProvider"
    );
    document.cookie = `${cookie}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;domain=${domain}`;
  });
};
